import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  message,
  Modal,
  Tooltip,
} from "antd";
import { EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import Component from "../../../Components";
import UserData from "../../../utils/UserData";
import Headers from "../../../redux/Actions/Headers/Header";
import { Maintenance } from "../../../redux/Actions";
import { useDispatch, useSelector } from "react-redux";

const { DynamicTable, Spinner } = Component;

const mapRow = (row, index) => ({
  id: row?.id ?? row?.maintenanceId ?? `maintenance-${index}`,
  isActive: row?.isActive,
  submittedByName:
    row?.submittedByName ??
    row?.submittedBy ??
    row?.userName ??
    row?.createdByName ??
    "-",
  submittedOn:
    row?.submittedOn ??
    row?.submittedAt ??
    row?.submissionDate ??
    row?.createdAt ??
    row?.whenSubmitted,
  startDateTime:
    row?.startDateTime ?? row?.maintenanceFrom ?? row?.startUtc ?? row?.from,
  endDateTime:
    row?.endDateTime ?? row?.maintenanceTill ?? row?.endUtc ?? row?.to,
  message: row?.message ?? row?.maintenanceMessage ?? row?.text ?? "",
});

/** Payload / DB: ISO-8601 in UTC. Ant Design DatePicker uses dayjs; normalize to Date then UTC ISO string. */
const toUtcIsoString = (value) => {
  const d = value?.toDate ? value.toDate() : value;
  return moment(d).toISOString();
};

/** Submitted-on timestamp in UTC (current instant). */
const nowUtcIsoString = () => moment.utc().toISOString();

/**
 * Table display: values are stored as UTC; show in the browser's local timezone.
 * Invalid or sentinel dates (e.g. DateTime.MinValue) show as "-".
 */
const formatUtcStoredAsLocal = (value) => {
  if (value == null || value === "") return "-";
  const m = moment.utc(value);
  if (!m.isValid()) return "-";
  if (m.year() < 1900) return "-";
  return m.local().format("MM/DD/YYYY hh:mm A");
};

const getMaintenanceStatus = (row) => {
  if (row?.isActive === false) return "Canceled";

  const start = moment.utc(row?.startDateTime);
  const end = moment.utc(row?.endDateTime);
  const now = moment.utc();

  if (!start.isValid() || !end.isValid()) return "-";
  if (start.isBefore(now) && end.isAfter(now)) return "Active";
  if (end.isBefore(now)) return "Completed";
  if (now.isBefore(start)) return "Upcoming";
  return "-";
};

const enrichRowWithStatus = (row) => ({
  ...row,
  status: getMaintenanceStatus(row),
});

const statusColors = {
  Active: "#389e0d",
  Upcoming: "#fa8c16",
  Canceled: "#f5222d",
  Completed: "#1d3d5c",
  "-": "#8c8c8c",
};

const getUtcTime = (value) => {
  const m = moment.utc(value);
  if (!m.isValid()) return 0;
  return m.valueOf();
};

const toMoment = (value) => {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : value;
  return moment(d);
};

const isPastDateTime = (value) => {
  const selected = toMoment(value);
  if (!selected?.isValid()) return false;
  return selected.isBefore(moment());
};

const disablePastDates = (current) => {
  if (!current) return false;
  return current.isBefore(moment().startOf("day"));
};

const getDisabledPastTime = (current) => {
  if (!current || !current.isSame(moment(), "day")) {
    return {};
  }

  const now = moment();
  const currentHour = now.hour();
  const currentMinute = now.minute();

  return {
    disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
    disabledMinutes: (selectedHour) =>
      selectedHour === currentHour
        ? Array.from({ length: currentMinute }, (_, i) => i)
        : [],
  };
};

const formatTimeForMessage = (m) => {
  if (!m?.isValid()) return "";
  if (m.minutes() === 0 && m.seconds() === 0) {
    return m.format("hA").toLowerCase();
  }
  return m.format("h:mm A").toLowerCase().replace(/\s/g, "");
};

/**
 * Default copy shown when both maintenance times are selected; users may edit before submit.
 */
const buildDefaultMaintenanceMessage = (start, end) => {
  const ms = toMoment(start);
  const me = toMoment(end);
  if (!ms?.isValid() || !me?.isValid()) return "";
  const startTime = formatTimeForMessage(ms);
  const endTime = formatTimeForMessage(me);
  const tail =
    ". All web site and portal/BoardVue access will be restored at that time. Thank you for your patience as we improve our system performance!";
  const lead =
    "The All Access system is undergoing scheduled maintenance and will be unavailable from ";
  if (ms.isSame(me, "day")) {
    const datePart = ms.format("dddd, MMMM Do");
    return `${lead}${startTime} to ${endTime} on ${datePart}${tail}`;
  }
  const startDatePart = ms.format("dddd, MMMM Do");
  const endDatePart = me.format("dddd, MMMM Do");
  return `${lead}${startTime} on ${startDatePart} to ${endTime} on ${endDatePart}${tail}`;
};

const MaintenanceSetup = () => {
  const dispatch = useDispatch();
  const user = UserData();
  const [form] = Form.useForm();
  const lastAutoMessageRef = useRef(null);
  const [activeTab, setActiveTab] = useState("setup");
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [messageValue, setMessageValue] = useState("");
  const { history, loadingHistory, loadingSubmit, loadingCancel } = useSelector(
    (state) => state?.Maintenance || {}
  );
  const rows = useMemo(
    () =>
      (history || []).map((row, index) => enrichRowWithStatus(mapRow(row, index))),
    [history]
  );
  const submitting = !!loadingSubmit;
  const listLoading = !!loadingHistory || !!loadingCancel;

  const loadHistory = useCallback(() => {
    dispatch(
      Maintenance.GetMaintenanceHistoryRequest({
        onError: (errorText) =>
          message.error(errorText || "Failed to load maintenance history."),
      })
    );
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      Headers.setHeader({
        title: "Schedule Maintenance",
        actions: [],
      })
    );
    return () => dispatch(Headers.resetHeader());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === "history") {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  const onFinish = (values) => {
    const start = values?.startDateTime;
    const end = values?.endDateTime;
    if (!start || !end) {
      message.error("Please select both a start and end date and time.");
      return;
    }
    if (isPastDateTime(start)) {
      message.error("Please select a future start date and time.");
      return;
    }
    if (isPastDateTime(end)) {
      message.error("Please select a future end date and time.");
      return;
    }
    if (end.isBefore(start) || end.isSame(start)) {
      message.error("The end date and time must be after the start.");
      return;
    }
    const userEmail = user.getUserInformation()?.displayName;
    const userName = user?.getUserName();
    const payload = {
      startDateTime: toUtcIsoString(start),
      endDateTime: toUtcIsoString(end),
      message: (values?.message || "").trim(),
      userEmail,
      userName,
      submittedOn: nowUtcIsoString(),
    };
    dispatch(
      Maintenance.SubmitMaintenanceRequest({
        request: payload,
        onSuccess: () => {
          message.success("Maintenance saved successfully.");
          lastAutoMessageRef.current = null;
          form.resetFields();
          loadHistory();
          setActiveTab("history");
        },
        onError: (errorText) =>
          message.error(errorText || "The maintenance window could not be saved. Please try again."),
      })
    );
  };

  const onFormValuesChange = (changed, allValues) => {
    if ("message" in changed) {
      setMessageValue(changed.message);
    }
    if (!("startDateTime" in changed || "endDateTime" in changed)) return;
    const start = allValues.startDateTime;
    const end = allValues.endDateTime;
    if (!start || !end) return;
    const next = buildDefaultMaintenanceMessage(start, end);
    if (!next) return;
    const current = form.getFieldValue("message");
    const trimmed = (current ?? "").trim();
    if (trimmed === "" || current === lastAutoMessageRef.current) {
      form.setFieldsValue({ message: next });
      lastAutoMessageRef.current = next;
      setMessageValue(next);
    }
  };

  const handleCancelMaintenance = useCallback(
    (row) => {
      if (!row?.id || Number.isNaN(Number(row.id))) {
        message.error("The maintenance entry contains invalid data.");
        return;
      }
      dispatch(
        Maintenance.CancelMaintenanceRequest({
          id: Number(row.id),
          onSuccess: () => {
            message.success("Maintenance canceled successfully.");
            loadHistory();
          },
          onError: (errorText) =>
            message.error(errorText || "The maintenance window could not be canceled. Please try again."),
        })
      );
    },
    [dispatch, loadHistory]
  );

  const columns = useMemo(
    () => [
      {
        title: "Submitted By",
        dataIndex: "submittedByName",
        key: "submittedByName",
        sorter: (a, b) =>
          (a?.submittedByName || "").localeCompare(b?.submittedByName || ""),
        ellipsis: true,
        width: 150,
      },
      {
        title: "Submitted On",
        dataIndex: "submittedOn",
        key: "submittedOn",
        sorter: (a, b) =>
          getUtcTime(a?.submittedOn) - getUtcTime(b?.submittedOn),
        render: (v) => formatUtcStoredAsLocal(v),
        width: 150,
      },
      {
        title: "Maintenance Start",
        dataIndex: "startDateTime",
        key: "startDateTime",
        sorter: (a, b) =>
          getUtcTime(a?.startDateTime) - getUtcTime(b?.startDateTime),
        render: (v) => formatUtcStoredAsLocal(v),
        width: 150,
      },
      {
        title: "Maintenance End",
        dataIndex: "endDateTime",
        key: "endDateTime",
        sorter: (a, b) =>
          getUtcTime(a?.endDateTime) - getUtcTime(b?.endDateTime),
        render: (v) => formatUtcStoredAsLocal(v),
        width: 150,
      },
      {
        title: "Message",
        dataIndex: "message",
        key: "message",
        sorter: (a, b) => (a?.message || "").localeCompare(b?.message || ""),
        width: 500,
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        filters: ["Active", "Upcoming", "Completed", "Canceled"].map(
          (status) => ({
            text: status,
            value: status,
          })
        ),
        onFilter: (value, record) => record?.status === value,
        render: (status) => {
          return (
            <span
              style={{
                color: statusColors[status] || statusColors["-"],
                fontWeight: 500,
              }}
            >
              {status}
            </span>
          );
        },
        width: 150,
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        render: (_, row) => {
          const status = row?.status;
          if (status !== "Upcoming") return null;

          return (
            <Button
              size="small"
              onClick={() => handleCancelMaintenance(row)}
              style={{
                minWidth: 84,
                borderRadius: 6,
                color: "#fff",
                backgroundColor: "#6c89a5",
                borderColor: "#6c89a5",
              }}
            >
              Cancel
            </Button>
          );
        },
        width: 150,
      },
    ],
    [handleCancelMaintenance]
  );

  const validateFutureDateTime = (_, value) => {
    if (!value) return Promise.resolve();
    if (isPastDateTime(value)) {
      return Promise.reject(
        new Error("Please select a future date/time, not a past time.")
      );
    }
    return Promise.resolve();
  };

  return (
    <>
      {(submitting || listLoading) && <Spinner />}
      <div className="user-table-box">
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Button
              type={activeTab === "setup" ? "primary" : "default"}
              onClick={() => setActiveTab("setup")}
              style={{ marginRight: 8 }}
            >
              Set Up Maintenance
            </Button>
            <Button
              type={activeTab === "history" ? "primary" : "default"}
              onClick={() => setActiveTab("history")}
            >
              Maintenance Log
            </Button>
          </div>
          {activeTab === "setup" && (
            <Tooltip
              title={
                !messageValue ? "Enter a message to preview" : "Live Preview"
              }
            >
              <Button
                shape="circle"
                icon={<EyeOutlined />}
                disabled={!messageValue}
                onClick={() => setIsPreviewVisible(true)}
                style={{
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: 100,
                }}
              />
            </Tooltip>
          )}
        </div>

        {activeTab === "setup" && (
          <Card style={{ marginBottom: 24 }}>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={onFormValuesChange}
              style={{ maxWidth: 720 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Maintenance Start"
                    name="startDateTime"
                    rules={[
                      { required: true, message: "Select start date and time" },
                      { validator: validateFutureDateTime },
                    ]}
                  >
                    <DatePicker
                      showTime
                      format="MM/DD/YYYY hh:mm A"
                      style={{ width: "100%" }}
                      disabledDate={disablePastDates}
                      disabledTime={getDisabledPastTime}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Maintenance End"
                    name="endDateTime"
                    rules={[
                      { required: true, message: "Select end date and time" },
                      { validator: validateFutureDateTime },
                    ]}
                  >
                    <DatePicker
                      showTime
                      format="MM/DD/YYYY hh:mm A"
                      style={{ width: "100%" }}
                      disabledDate={disablePastDates}
                      disabledTime={getDisabledPastTime}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item
                label="Maintenance Message"
                name="message"
                rules={[
                  { required: true, message: "Enter a maintenance message" },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  Confirm Schedule
                </Button>
              </Form.Item>
            </Form>
          </Card>
        )}

        {activeTab === "history" && (
          <Card>
            <DynamicTable
              rowKey="id"
              onRow={() => ({})}
              data={rows}
              columns={columns}
              needDefaultWidth
              pagination={
                rows.length > 0
                  ? { pageSize: 10, showSizeChanger: true }
                  : false
              }
              onChange={() => {}}
            />
          </Card>
        )}

        <Modal
          title="Live Preview"
          visible={isPreviewVisible}
          onCancel={() => setIsPreviewVisible(false)}
          footer={null}
          width="70vw"
          centered
        >
          <div style={{ textAlign: "center", padding: "20px" }}>
            <img
              src="/Maintenance-Illustration.png"
              alt="Maintenance Illustration"
              style={{ width: "300px", marginBottom: "24px" }}
            />
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "600",
                marginBottom: "16px",
              }}
            >
              Scheduled Maintenance
            </h2>
            <p
              style={{
                fontSize: "16px",
                color: "#595959",
                marginBottom: "32px",
                lineHeight: "1.6",
              }}
            >
              {messageValue}
            </p>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default MaintenanceSetup;
